"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.renderMerkleTree = exports.isValidMerkleTree = exports.processMultiProof = exports.getMultiProof = exports.processProof = exports.getProof = exports.makeMerkleTree = void 0;
const keccak_1 = require("ethereum-cryptography/keccak");
const utils_1 = require("ethereum-cryptography/utils");
const bytes_1 = require("./bytes");
const throw_error_1 = require("./utils/throw-error");
const hashPair = (a, b) => (0, keccak_1.keccak256)((0, utils_1.concatBytes)(...[a, b].sort(bytes_1.compareBytes)));
const leftChildIndex = (i) => 2 * i + 1;
const rightChildIndex = (i) => 2 * i + 2;
const parentIndex = (i) => i > 0 ? Math.floor((i - 1) / 2) : (0, throw_error_1.throwError)('Root has no parent');
const siblingIndex = (i) => i > 0 ? i - (-1) ** (i % 2) : (0, throw_error_1.throwError)('Root has no siblings');
const isTreeNode = (tree, i) => i >= 0 && i < tree.length;
const isInternalNode = (tree, i) => isTreeNode(tree, leftChildIndex(i));
const isLeafNode = (tree, i) => isTreeNode(tree, i) && !isInternalNode(tree, i);
const isValidMerkleNode = (node) => node instanceof Uint8Array && node.length === 32;
const checkTreeNode = (tree, i) => void (isTreeNode(tree, i) || (0, throw_error_1.throwError)('Index is not in tree'));
const checkInternalNode = (tree, i) => void (isInternalNode(tree, i) || (0, throw_error_1.throwError)('Index is not an internal tree node'));
const checkLeafNode = (tree, i) => void (isLeafNode(tree, i) || (0, throw_error_1.throwError)('Index is not a leaf'));
const checkValidMerkleNode = (node) => void (isValidMerkleNode(node) || (0, throw_error_1.throwError)('Merkle tree nodes must be Uint8Array of length 32'));
function makeMerkleTree(leaves) {
    leaves.forEach(checkValidMerkleNode);
    if (leaves.length === 0) {
        throw new Error('Expected non-zero number of leaves');
    }
    const tree = new Array(2 * leaves.length - 1);
    for (const [i, leaf] of leaves.entries()) {
        tree[tree.length - 1 - i] = leaf;
    }
    for (let i = tree.length - 1 - leaves.length; i >= 0; i--) {
        tree[i] = hashPair(tree[leftChildIndex(i)], tree[rightChildIndex(i)]);
    }
    return tree;
}
exports.makeMerkleTree = makeMerkleTree;
function getProof(tree, index) {
    checkLeafNode(tree, index);
    const proof = [];
    while (index > 0) {
        proof.push(tree[siblingIndex(index)]);
        index = parentIndex(index);
    }
    return proof;
}
exports.getProof = getProof;
function processProof(leaf, proof) {
    checkValidMerkleNode(leaf);
    proof.forEach(checkValidMerkleNode);
    return proof.reduce(hashPair, leaf);
}
exports.processProof = processProof;
function getMultiProof(tree, indices) {
    indices.forEach(i => checkLeafNode(tree, i));
    indices.sort((a, b) => b - a);
    if (indices.slice(1).some((i, p) => i === indices[p])) {
        throw new Error('Cannot prove duplicated index');
    }
    const stack = indices.concat(); // copy
    const proof = [];
    const proofFlags = [];
    while (stack.length > 0 && stack[0] > 0) {
        const j = stack.shift(); // take from the beginning
        const s = siblingIndex(j);
        const p = parentIndex(j);
        if (s === stack[0]) {
            proofFlags.push(true);
            stack.shift(); // consume from the stack
        }
        else {
            proofFlags.push(false);
            proof.push(tree[s]);
        }
        stack.push(p);
    }
    if (indices.length === 0) {
        proof.push(tree[0]);
    }
    return {
        leaves: indices.map(i => tree[i]),
        proof,
        proofFlags,
    };
}
exports.getMultiProof = getMultiProof;
function processMultiProof(multiproof) {
    multiproof.leaves.forEach(checkValidMerkleNode);
    multiproof.proof.forEach(checkValidMerkleNode);
    if (multiproof.proof.length < multiproof.proofFlags.filter(b => !b).length) {
        throw new Error('Invalid multiproof format');
    }
    if (multiproof.leaves.length + multiproof.proof.length !== multiproof.proofFlags.length + 1) {
        throw new Error('Provided leaves and multiproof are not compatible');
    }
    const stack = multiproof.leaves.concat(); // copy
    const proof = multiproof.proof.concat(); // copy
    for (const flag of multiproof.proofFlags) {
        const a = stack.shift();
        const b = flag ? stack.shift() : proof.shift();
        stack.push(hashPair(a, b));
    }
    return stack.pop() ?? proof.shift();
}
exports.processMultiProof = processMultiProof;
function isValidMerkleTree(tree) {
    for (const [i, node] of tree.entries()) {
        if (!isValidMerkleNode(node)) {
            return false;
        }
        const l = leftChildIndex(i);
        const r = rightChildIndex(i);
        if (r >= tree.length) {
            if (l < tree.length) {
                return false;
            }
        }
        else if (!(0, utils_1.equalsBytes)(node, hashPair(tree[l], tree[r]))) {
            return false;
        }
    }
    return tree.length > 0;
}
exports.isValidMerkleTree = isValidMerkleTree;
function renderMerkleTree(tree) {
    if (tree.length === 0) {
        throw new Error('Expected non-zero number of nodes');
    }
    const stack = [[0, []]];
    const lines = [];
    while (stack.length > 0) {
        const [i, path] = stack.pop();
        lines.push(path.slice(0, -1).map(p => ['   ', '│  '][p]).join('') +
            path.slice(-1).map(p => ['└─ ', '├─ '][p]).join('') +
            i + ') ' +
            (0, utils_1.bytesToHex)(tree[i]));
        if (rightChildIndex(i) < tree.length) {
            stack.push([rightChildIndex(i), path.concat(0)]);
            stack.push([leftChildIndex(i), path.concat(1)]);
        }
    }
    return lines.join('\n');
}
exports.renderMerkleTree = renderMerkleTree;
//# sourceMappingURL=core.js.map